import { Component } from '@theme/component';
import { loadProfile, recommendSize, saveProfile } from '@theme/charm-size-logic';

const TITLES = [
  'Conte um pouco sobre você',
  'Qual o formato do seu quadril?',
  'Como você gosta de vestir?',
  'Encontramos seu tamanho',
];

/** localStorage pode lançar só de ser acessado (Safari com cookies bloqueados). */
function safeStorage() {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/**
 * "Provador virtual": orquestra os 4 passos e aplica o tamanho no seletor de variantes do Horizon.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement[]} steps
 * @property {HTMLElement} badge
 * @property {HTMLElement} cta
 * @property {HTMLElement} stepLabel
 * @property {HTMLElement} title
 * @property {HTMLInputElement} height
 * @property {HTMLInputElement} weight
 * @property {HTMLOutputElement} heightOut
 * @property {HTMLOutputElement} weightOut
 * @property {HTMLElement} resultSize
 * @property {HTMLElement} resultNote
 * @property {HTMLButtonElement} back
 * @property {HTMLButtonElement} next
 *
 * @extends {Component<Refs>}
 */
class CharmSizeAdvisor extends Component {
  requiredRefs = ['dialog', 'steps', 'badge', 'cta', 'height', 'weight', 'resultSize', 'back', 'next'];

  #step = 0;
  #sizes = [];
  #result = null;

  connectedCallback() {
    super.connectedCallback();
    try {
      this.#sizes = JSON.parse(this.dataset.sizes ?? '[]');
    } catch {
      this.#sizes = [];
    }
    // Grade não reconhecida (ex.: 36/38/40): sem recomendação possível, some.
    if (recommendSize({ heightCm: 165, weightKg: 62, hip: 0, fit: 0 }, this.#sizes) === null) {
      this.hidden = true;
      return;
    }
    const profile = loadProfile(safeStorage());
    if (profile) {
      this.refs.height.value = String(profile.heightCm);
      this.refs.weight.value = String(profile.weightKg);
      this.#setRadio('hip', profile.hip);
      this.#setRadio('fit', profile.fit);
      this.#showSuggestion(recommendSize(profile, this.#sizes));
    }
    this.sync();
  }

  open() {
    this.#goTo(0);
    this.refs.dialog.showModal();
  }

  sync() {
    if (this.refs.heightOut) this.refs.heightOut.value = this.refs.height.value;
    if (this.refs.weightOut) this.refs.weightOut.value = this.refs.weight.value;
  }

  back() {
    if (this.#step === 0) return this.refs.dialog.close();
    this.#goTo(this.#step === 3 ? 0 : this.#step - 1);
  }

  next() {
    if (this.#step < 2) return this.#goTo(this.#step + 1);
    if (this.#step === 2) {
      const body = this.#body();
      this.#result = recommendSize(body, this.#sizes);
      saveProfile(safeStorage(), body);
      this.#renderResult();
      return this.#goTo(3);
    }
    this.#applySize(this.#result.size);
    this.#showSuggestion(this.#result);
    this.refs.dialog.close();
  }

  #body() {
    return {
      heightCm: Number(this.refs.height.value),
      weightKg: Number(this.refs.weight.value),
      hip: this.#radio('hip'),
      fit: this.#radio('fit'),
    };
  }

  #radio(field) {
    const checked = this.querySelector(`input[name^="${field}-"]:checked`);
    return checked ? Number(checked.value) : 0;
  }

  #setRadio(field, value) {
    const input = this.querySelector(`input[name^="${field}-"][value="${value}"]`);
    if (input) input.checked = true;
  }

  #goTo(step) {
    this.#step = step;
    this.refs.steps.forEach((el, i) => (el.hidden = i !== step));
    if (this.refs.stepLabel) this.refs.stepLabel.textContent = `${step + 1} de 4`;
    if (this.refs.title) this.refs.title.textContent = TITLES[step];
    this.refs.back.textContent = step === 0 ? 'Fechar' : step === 3 ? 'Refazer' : 'Voltar';
    this.refs.next.textContent = step === 3 ? `Usar tamanho ${this.#result.size.trim()}` : 'Continuar';
  }

  #renderResult() {
    const { size, between } = this.#result;
    this.refs.resultSize.textContent = size.trim();
    if (!this.refs.resultNote) return;
    if (!between) {
      this.refs.resultNote.textContent = `O ${size.trim()} deve vestir na medida.`;
      return;
    }
    const [smaller, larger] = between.map((s) => s.trim());
    this.refs.resultNote.textContent = `Você está entre ${smaller} e ${larger}. O ${smaller} veste mais justo; o ${larger}, mais confortável.`;
  }

  #showSuggestion(result) {
    if (!result) return;
    this.refs.badge.textContent = result.size.trim();
    this.refs.cta.textContent = `Sugerido: ${result.size.trim()} · refazer`;
  }

  /** Clica no botão de tamanho do seletor do Horizon, para seguir o fluxo nativo de variante. */
  #applySize(size) {
    const scope = this.closest('.product-details') ?? document;
    const name = CSS.escape(this.dataset.optionName ?? '');
    const input = scope.querySelector(`input[type="radio"][data-option-name="${name}"][value="${CSS.escape(size)}"]`);
    if (input && !input.checked) input.click();
  }
}

if (!customElements.get('charm-size-advisor-component')) {
  customElements.define('charm-size-advisor-component', CharmSizeAdvisor);
}
